import React, { useEffect, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { app } from '../../lib/firebase.js';

export default function FirebaseAuthBridge({ children }) {
  const { getToken } = useAuth();
  const { user, isLoaded: isUserLoaded, isSignedIn } = useUser();
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const firebaseAuth = app ? getAuth(app) : null;

    const syncFirebaseSession = async () => {
      if (!isUserLoaded) return;
      if (!isSignedIn || !user?.id || !firebaseAuth) {
        if (firebaseAuth && !isSignedIn) await signOut(firebaseAuth).catch(() => {});
        if (!cancelled) setIsFirebaseReady(true);
        return;
      }

      try {
        const clerkToken = await getToken();
        const response = await fetch('/api/firebase-token', {
          method: 'POST',
          headers: { Authorization: `Bearer ${clerkToken}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.token) throw new Error(data.error || 'Firebase session could not be created');
        await signInWithCustomToken(firebaseAuth, data.token);
        if (!cancelled) {
          setError(null);
          setIsFirebaseReady(true);
        }
      } catch (syncError) {
        console.error('[firebase-bridge] Firebase session sync failed', syncError);
        // A Firebase session is required for private writes, but it must not
        // prevent a public story link from rendering. Clear any stale client
        // session and let the app surface its normal public/private access
        // state instead of trapping the user on a refresh-only screen.
        await signOut(firebaseAuth).catch(() => {});
        if (!cancelled) {
          setError(syncError.code === 'auth/missing-project-id'
            ? 'Your account session needs to be reconnected.'
            : 'Your account data could not be synced right now.');
          setIsFirebaseReady(true);
        }
      }
    };

    syncFirebaseSession();
    return () => { cancelled = true; };
  }, [getToken, isSignedIn, isUserLoaded, user?.id]);

  return (
    <>
      {children}
      {error && isFirebaseReady && (
        <div
          role="status"
          style={{
            position: 'fixed',
            left: 16,
            right: 16,
            bottom: 16,
            zIndex: 1500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '12px 16px',
            border: '1px solid #e7d5a0',
            borderRadius: 12,
            background: '#fff8e7',
            color: '#5a4316',
            boxShadow: '0 8px 28px rgba(45, 36, 28, 0.14)',
            fontSize: 14,
          }}
        >
          <span>{error} Public stories are still available to read. Sign out and back in to restore account features.</span>
        </div>
      )}
    </>
  );
}
